import { useMemo } from 'react';

export type BrowserType = 'chrome' | 'firefox' | 'safari' | 'edge' | 'opera' | 'unknown';
export type OSType = 'windows' | 'macos' | 'linux' | 'android' | 'ios' | 'unknown';

export interface BrowserInfo {
  browser: BrowserType;
  browserName: string;
  browserVersion: string;
  os: OSType;
  osName: string;
  isMobile: boolean;
  supportsScreenShare: boolean;
  supportsSystemAudio: boolean;
  supportsSpeechRecognition: boolean;
}

const useBrowserDetection = (): BrowserInfo => {
  return useMemo(() => {
    const ua = navigator.userAgent;
    const vendor = navigator.vendor || '';
    
    // Detect browser
    let browser: BrowserType = 'unknown';
    let browserName = 'Unknown Browser';
    let browserVersion = '';

    if (ua.includes('Edg/')) {
      browser = 'edge';
      browserName = 'Microsoft Edge';
      browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
    } else if (ua.includes('OPR/') || ua.includes('Opera')) {
      browser = 'opera';
      browserName = 'Opera';
      browserVersion = ua.match(/OPR\/(\d+\.\d+)/)?.[1] || '';
    } else if (ua.includes('Chrome/') && vendor.includes('Google')) {
      browser = 'chrome';
      browserName = 'Google Chrome';
      browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
    } else if (ua.includes('Firefox/')) {
      browser = 'firefox';
      browserName = 'Mozilla Firefox';
      browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      browser = 'safari';
      browserName = 'Safari';
      browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || '';
    }

    // Detect OS
    let os: OSType = 'unknown';
    let osName = 'Unknown OS';

    if (ua.includes('Windows')) {
      os = 'windows';
      osName = 'Windows';
    } else if (ua.includes('Mac OS X') || ua.includes('Macintosh')) {
      if (ua.includes('iPhone') || ua.includes('iPad')) {
        os = 'ios';
        osName = 'iOS';
      } else {
        os = 'macos';
        osName = 'macOS';
      }
    } else if (ua.includes('Android')) {
      os = 'android';
      osName = 'Android';
    } else if (ua.includes('Linux')) {
      os = 'linux';
      osName = 'Linux';
    }

    // Detect mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    // Feature support
    const supportsScreenShare = !!(navigator.mediaDevices && 'getDisplayMedia' in navigator.mediaDevices);
    
    // System audio is best supported on Chrome/Edge on Windows/ChromeOS
    const supportsSystemAudio = (browser === 'chrome' || browser === 'edge') && 
      (os === 'windows' || ua.includes('CrOS'));
    
    // Speech recognition support
    const supportsSpeechRecognition = !!(
      (window as any).SpeechRecognition || 
      (window as any).webkitSpeechRecognition
    );

    return {
      browser,
      browserName,
      browserVersion,
      os,
      osName,
      isMobile,
      supportsScreenShare,
      supportsSystemAudio,
      supportsSpeechRecognition,
    };
  }, []);
};

export default useBrowserDetection;
